import { useState, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, X, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGES = 3;
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export default function ProductImageUploader({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const list = images || [];

  const handleFiles = async (files) => {
    const remaining = MAX_IMAGES - list.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images`);
      return;
    }
    const arr = Array.from(files).slice(0, remaining);
    setUploading(true);
    const next = [...list];
    for (const file of arr) {
      if (!ALLOWED.includes(file.type)) {
        toast.error(`${file.name}: format non supporté (PNG, JPG, WebP)`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: max 5 Mo`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/uploads/product-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(r.data.url);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur upload");
      }
    }
    onChange(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next);
  };

  const setMain = (idx) => {
    if (idx === 0) return;
    const next = [list[idx], ...list.filter((_, i) => i !== idx)];
    onChange(next);
  };

  return (
    <div data-testid="product-image-uploader">
      <div className="grid grid-cols-3 gap-3 mb-3">
        {list.map((url, idx) => (
          <div key={url + idx} className={`relative aspect-square rounded-xl overflow-hidden border ${idx === 0 ? "border-[#7AC143] border-2" : "border-[#E5EAE5]"}`} data-testid={`uploaded-image-${idx}`}>
            <img src={url} alt="" className="w-full h-full object-cover" />
            {idx === 0 && (
              <span className="absolute top-1 left-1 bg-[#7AC143] text-white text-[10px] font-bold uppercase px-1.5 py-0.5 rounded flex items-center gap-1">
                <Star className="w-2.5 h-2.5 fill-current" /> Principale
              </span>
            )}
            <button type="button" onClick={() => removeAt(idx)} data-testid={`remove-image-${idx}`} className="absolute top-1 right-1 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
            {idx > 0 && (
              <button type="button" onClick={() => setMain(idx)} data-testid={`set-main-${idx}`} className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-[10px] font-bold uppercase py-1 rounded hover:bg-[#7AC143]">
                Définir principale
              </button>
            )}
          </div>
        ))}
        {list.length < MAX_IMAGES && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-image-btn"
            className="aspect-square border-2 border-dashed border-[#E5EAE5] rounded-xl flex flex-col items-center justify-center gap-2 text-[#4B634B] hover:border-[#7AC143] hover:text-[#7AC143] transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
            <span className="text-xs font-semibold">{uploading ? "Upload..." : "Ajouter"}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid="image-file-input"
      />
      <p className="text-xs text-[#4B634B]">PNG, JPG ou WebP · max 5 Mo · jusqu'à {MAX_IMAGES} images · 1ʳᵉ = principale (affichée par défaut)</p>
    </div>
  );
}
